import React, { useState } from 'react';
import { Wave, WaveformType } from '../../types';
import { useCanvasAnimation } from '../../hooks/useCanvasAnimation';
import { RangeSlider } from '../ui/RangeSlider';
import configService from '../../services/configService';

const waveformOptions: { label: string; value: WaveformType }[] = [
    { label: 'Sine', value: 'sine' },
    { label: 'Square', value: 'square' },
    { label: 'Triangle', value: 'triangle' },
    { label: 'Saw', value: 'sawtooth' }
];

const sampleWaveform = (waveform: WaveformType | undefined, angle: number): number => {
    switch (waveform) {
        case 'square': {
            const v = Math.sin(angle);
            return v >= 0 ? 1 : -1;
        }
        case 'triangle':
            return (2 / Math.PI) * Math.asin(Math.sin(angle));
        case 'sawtooth': {
            const norm = ((angle / (2 * Math.PI)) % 1 + 1) % 1;
            return 2 * norm - 1;
        }
        default:
            return Math.sin(angle);
    }
};

interface WaveCardProps {
    wave: Wave;
    onChange: (id: number, updates: Partial<Wave>) => void;
    onRemove: (id: number) => void;
    onCounter: (id: number) => void;
    isRemovable: boolean;
}

export const WaveCard: React.FC<WaveCardProps> = ({ wave, onChange, onRemove, onCounter, isRemovable }) => {
    const [showSaveFavoriteDialog, setShowSaveFavoriteDialog] = useState(false);
    const [favoriteName, setFavoriteName] = useState('');
    const [hasEditedName, setHasEditedName] = useState(false);
    
    // Check for potentially damaging frequency ranges
    const isDangerousFreq = wave.freq > 15000 || wave.freq < 20;
    const isHighAmplitude = wave.amp > 75;
    const showWarning = !wave.muted && (isDangerousFreq || isHighAmplitude);
    
    // Updated signature: added deltaTime (unused here)
    const canvasRef = useCanvasAnimation((ctx, time, deltaTime, width, height) => {
        const cy = height / 2;
        ctx.clearRect(0, 0, width, height);

        // Draw only the wave, no background grid line
        ctx.beginPath();
        // If muted, show gray/dashed line to indicate potential wave
        ctx.strokeStyle = wave.muted ? '#d1d5db' : wave.color;
        ctx.lineWidth = 2;
        if (wave.muted) ctx.setLineDash([4, 4]);
        else ctx.setLineDash([]);
        
        const waveform = wave.waveform || 'sine';
        // Optimize drawing: waveform-aware plot
        for (let x = 0; x < width; x++) { 
            const t = x / width;
            const phaseRad = (wave.phase * Math.PI) / 180;
            // Always show the wave form even if muted, just styled differently
            const angle = 2 * Math.PI * wave.freq * t + phaseRad + (time * 2);
            const yOffset = (wave.amp / 100) * (height / 2.5) * sampleWaveform(waveform, angle);
            
            if (x === 0) ctx.moveTo(x, cy - yOffset);
            else ctx.lineTo(x, cy - yOffset);
        }
        ctx.stroke();
        ctx.setLineDash([]);
    }, [wave]); 

    const handleSaveFavorite = () => {
        const nameToSave = favoriteName.trim() || `${wave.freq}Hz @ ${wave.amp}%`;
        
        try {
            configService.saveFavoriteFrequency(nameToSave, wave);
            setShowSaveFavoriteDialog(false);
            setFavoriteName('');
            setHasEditedName(false);
            // Optional: Show success message
            alert('Frequency saved to favorites!');
        } catch (error) {
            console.error('Failed to save favorite:', error);
            alert('Failed to save favorite. Please try again.');
        }
    };

    const handleOpenSaveFavoriteDialog = () => {
        setShowSaveFavoriteDialog(true);
        setFavoriteName(`${wave.freq}Hz @ ${wave.amp}%`);
        setHasEditedName(false);
    };

    const handleNameFocus = () => {
        if (!hasEditedName) {
            setFavoriteName('');
            setHasEditedName(true);
        }
    };

    return (
        <div className={`bg-white rounded-xl shadow-sm border p-3 relative overflow-hidden transition-all duration-300 hover:shadow-md ${wave.muted ? 'border-gray-300 opacity-75' : 'border-gray-300'}`}>
            <div className={`absolute left-0 top-0 bottom-0 w-1 transition-colors ${wave.muted ? 'bg-gray-300' : ''}`} style={{ backgroundColor: wave.muted ? undefined : wave.color }}></div>
            
            <div className="flex justify-between items-start mb-2 pl-2">
                <div className={`text-xs font-bold uppercase tracking-wider transition-colors ${wave.muted ? 'text-gray-400' : ''}`} style={{ color: wave.muted ? undefined : wave.color }}>
                    Frequency {wave.id} {wave.muted && '(Muted)'}
                </div>
                <div className="flex gap-1">
                    {/* Channel Toggle */}
                    <button 
                        onClick={() => {
                            const currentChannel = wave.channel || 'both';
                            const nextChannel = currentChannel === 'both' ? 'left' : currentChannel === 'left' ? 'right' : 'both';
                            onChange(wave.id, { channel: nextChannel });
                        }}
                        title={`Channel: ${wave.channel || 'both'}`}
                        className={`transition-colors px-2 py-1 rounded text-xs font-semibold ${wave.muted ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100' : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'}`}
                    >
                        {wave.channel === 'left' ? 'L' : wave.channel === 'right' ? 'R' : 'LR'}
                    </button>
                    <button 
                        onClick={() => onChange(wave.id, { muted: !wave.muted })}
                        title={wave.muted ? "Unmute" : "Mute"}
                        className={`transition-colors p-1.5 rounded ${wave.muted ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100' : 'text-gray-500 hover:text-indigo-600 hover:bg-indigo-50'}`}
                    >
                        {wave.muted ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                        )}
                    </button>
                    <button 
                        onClick={() => onCounter(wave.id)}
                        title="Add Cancellation Wave (180° phase shift)"
                        className="text-gray-400 hover:text-red-500 transition-colors p-1.5 rounded hover:bg-red-50"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                    </button>
                    <button 
                        onClick={() => handleOpenSaveFavoriteDialog()}
                        title="Save as Favorite"
                        className="text-gray-400 hover:text-pink-500 transition-colors p-1.5 rounded hover:bg-pink-50"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                    </button>
                    {isRemovable && (
                        <button 
                            onClick={() => onRemove(wave.id)}
                            title="Remove Layer"
                            className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded hover:bg-gray-100"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            {/* Warning for potentially damaging frequencies/amplitudes */}
            {showWarning && (
                <div className="mx-2 mb-3 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                    <div className="flex items-start gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div className="text-xs text-yellow-800">
                            <strong className="font-semibold">Warning:</strong>{' '}
                            {isDangerousFreq && wave.freq > 15000 && 'High frequency (>15kHz) can be painful at high volume. '}
                            {isDangerousFreq && wave.freq < 20 && 'Infrasound (<20Hz) may cause discomfort. '}
                            {isHighAmplitude && 'High amplitude (>75%) can damage hearing. '}
                            Lower volume before playing.
                        </div>
                    </div>
                </div>
            )}

            {/* Mini Visualizer */}
            <div className={`h-16 w-full bg-gray-50 rounded-lg overflow-hidden border border-gray-100 relative mb-3 transition-opacity ${wave.muted ? 'opacity-50 grayscale' : ''}`}>
                <canvas ref={canvasRef} className="w-full h-full block" />
            </div>

            {/* Controls */}
            <div className={`space-y-3 pl-2 transition-opacity ${wave.muted ? 'opacity-50 pointer-events-none' : ''}`}>
                <RangeSlider 
                    label="Frequency (Hz)" 
                    min={20} max={20000} step={1}
                    value={wave.freq}
                    color={wave.color}
                    onChange={(e) => onChange(wave.id, { freq: parseFloat(e.target.value) })}
                />
                <RangeSlider 
                    label="Amplitude (%)" 
                    min={0} max={100}
                    value={wave.amp}
                    color={wave.color}
                    onChange={(e) => onChange(wave.id, { amp: parseFloat(e.target.value) })}
                />
                <RangeSlider 
                    label="Phase (°)" 
                    min={0} max={360}
                    value={wave.phase}
                    color={wave.color}
                    onChange={(e) => onChange(wave.id, { phase: parseFloat(e.target.value) })}
                />

                {/* Waveform selector */}
                <div className="pt-1">
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-500">Waveform</span>
                        <div className="flex gap-1 flex-wrap justify-end">
                            {waveformOptions.slice(0, 2).map(option => {
                                const active = (wave.waveform || 'sine') === option.value;
                                return (
                                    <button
                                        key={option.value}
                                        onClick={() => onChange(wave.id, { waveform: option.value })}
                                        className={`px-2 py-0.5 rounded text-xs font-semibold transition-colors border ${
                                            active
                                                ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                                : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                                        }`}
                                        title={`${option.label} wave`}
                                    >
                                        {option.label}
                                    </button>
                                );
                            })}
                            <div className="basis-full h-0"></div>
                            {waveformOptions.slice(2).map(option => {
                                const active = (wave.waveform || 'sine') === option.value;
                                return (
                                    <button
                                        key={option.value}
                                        onClick={() => onChange(wave.id, { waveform: option.value })}
                                        className={`px-2 py-0.5 rounded text-xs font-semibold transition-colors border ${
                                            active
                                                ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                                : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                                        }`}
                                        title={`${option.label} wave`}
                                    >
                                        {option.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
                
                {/* Auto-Pan (EMDR/Bilateral) Controls */}
                <div className="pt-2 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-500">Auto-Pan (Bilateral)</span>
                        <button
                            onClick={() => {
                                if ((wave.autoPanRate || 0) > 0) {
                                    onChange(wave.id, { autoPanRate: 0, autoPanDepth: 0 });
                                } else {
                                    onChange(wave.id, { autoPanRate: 1, autoPanDepth: 100 });
                                }
                            }}
                            className={`text-xs px-2 py-0.5 rounded transition-colors ${
                                (wave.autoPanRate || 0) > 0 
                                    ? 'bg-purple-100 text-purple-700' 
                                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                        >
                            {(wave.autoPanRate || 0) > 0 ? 'ON' : 'OFF'}
                        </button>
                    </div>
                    {(wave.autoPanRate || 0) > 0 && (
                        <>
                            <RangeSlider 
                                label="Pan Rate (Hz)" 
                                min={0.1} max={4} step={0.1}
                                value={wave.autoPanRate || 1}
                                color="#8B5CF6"
                                onChange={(e) => onChange(wave.id, { autoPanRate: parseFloat(e.target.value) })}
                            />
                            <RangeSlider 
                                label="Pan Depth (%)" 
                                min={0} max={100}
                                value={wave.autoPanDepth || 100}
                                color="#8B5CF6"
                                onChange={(e) => onChange(wave.id, { autoPanDepth: parseFloat(e.target.value) })}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Save as Favorite Modal */}
            {showSaveFavoriteDialog && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
                    style={{ zIndex: 10000 }}
                    onClick={() => {
                        setShowSaveFavoriteDialog(false);
                        setFavoriteName('');
                        setHasEditedName(false);
                    }}
                >
                    <div 
                        className="bg-white rounded-lg p-6 w-full max-w-sm shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-pink-500" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                </svg>
                                Save as Favorite
                            </h3>
                            <button
                                onClick={() => {
                                    setShowSaveFavoriteDialog(false);
                                    setHasEditedName(false);
                                    setFavoriteName('');
                                }}
                                className="text-gray-500 hover:text-gray-700 text-xl"
                            >
                                ×
                            </button>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Name this frequency
                            </label>
                            <input
                                type="text"
                                value={favoriteName}
                                onChange={(e) => {
                                    setFavoriteName(e.target.value);
                                    setHasEditedName(true);
                                }}
                                onFocus={handleNameFocus}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSaveFavorite();
                                    }
                                }}
                                placeholder={`${wave.freq}Hz @ ${wave.amp}%`}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                                autoFocus
                            />
                        </div>

                        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                            <div className="text-xs text-gray-600 space-y-1">
                                <div><strong>Frequency:</strong> {wave.freq} Hz</div>
                                <div><strong>Amplitude:</strong> {wave.amp}%</div>
                                <div><strong>Phase:</strong> {wave.phase}°</div>
                                <div><strong>Channel:</strong> {wave.channel || 'both'}</div>
                            </div>
                        </div>

                        <div className="flex space-x-3">
                            <button
                                onClick={() => {
                                    setShowSaveFavoriteDialog(false);
                                    setFavoriteName('');
                                    setHasEditedName(false);
                                }}
                                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveFavorite}
                                className="flex-1 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};